/**
 * ErrorState — the one way a failed load looks in ClickPay.
 *
 * Audit HUB-01 showed the Accounts tab printing "500 : Internal Server
 * Error" in a table cell. Every page had its own way of showing `String(e)`,
 * and none of them told a person what to do next. This component turns an
 * `ApiError` (src/lib/query/apiError.ts) into a plain-English sentence
 * (src/lib/query/describeError.ts), a Retry button, and — for admins only —
 * the raw message and the server trace id under a "Technical details"
 * disclosure, so a screenshot from an employee and a log line on the mini
 * can be matched up.
 *
 * Renders nothing for a cancelled request: cancellation is not a failure.
 */
import type { ReactNode } from 'react'
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'
import { cn } from '../lib/cn'
import { Button } from './ui'
import { describeError } from '../lib/query/describeError'
import { isAbortError } from '../lib/query/requestSignal'
import { useSessionInfo } from '../lib/query/session'

export function ErrorState({
  error, onRetry, what, compact = false, className, children,
}: {
  error: unknown
  /** Called by the Retry button; the button is hidden when omitted. */
  onRetry?: () => void
  /** What was being loaded, in plain words: "the Accounts list". */
  what?: string
  /** One-line inline variant for table cells and tiles. */
  compact?: boolean
  className?: string
  children?: ReactNode
}) {
  const { isAdmin } = useSessionInfo()
  if (isAbortError(error)) return null
  const d = describeError(error, what)
  const Icon = d.offline ? WifiOff : AlertTriangle

  if (compact) {
    return (
      <div role="alert" className={cn('inline-flex items-center gap-2 text-[13px] text-ink-secondary', className)}>
        <Icon size={14} className="text-danger shrink-0" aria-hidden />
        <span>{d.title}</span>
        {onRetry && (
          <button type="button" onClick={onRetry} className="text-accent hover:underline font-medium">
            Retry
          </button>
        )}
      </div>
    )
  }

  return (
    <div role="alert" className={cn('rounded-xl border border-edge bg-surface p-5 max-w-xl', className)}>
      <div className="flex items-start gap-3">
        <Icon size={20} className="text-danger shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink">{d.title}</div>
          {d.hint && <div className="mt-1 text-sm text-ink-secondary">{d.hint}</div>}
          {d.reference && (
            <div className="mt-2 text-xs text-ink-muted tabular-nums">Reference: {d.reference}</div>
          )}
          {children}
          <div className="mt-3 flex items-center gap-3">
            {onRetry && (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                <RefreshCw size={14} aria-hidden /> Retry
              </Button>
            )}
            {isAdmin && (
              <details className="text-xs text-ink-muted">
                <summary className="cursor-pointer select-none hover:text-ink">Technical details</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words rounded-md bg-surface-2 p-2 text-[11px] text-ink-secondary">
                  {d.technical}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
